import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  IconButton,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Divider,
  Chip,
  MenuItem,
  CircularProgress,
} from "@mui/material";

import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MeetingRoom as RoomIcon,
} from "@mui/icons-material";

import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  doc,
  updateDoc,
  deleteDoc,
  Timestamp,
} from "firebase/firestore";

import { getAuth } from "firebase/auth";
import Swal from "sweetalert2";
import { db } from "../../firebase";

const PRIMARY = "#0d9488";

export default function Hostelrooms() {
  const auth = getAuth();

  const [rooms, setRooms] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [hostelId, setHostelId] = useState("");

  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const [search, setSearch] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const currentSchoolId = "zIlbJM5BjyrlEhg7fWKs";

  const [form, setForm] = useState({
    hostelId: "",
    roomNo: "",
    noOfBeds: "",
    maxStudents: "",
    status: "Available",
    schoolId: currentSchoolId,
  });

  useEffect(() => {
    loadCurrentUser();
  }, []);

  const loadCurrentUser = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      const q = query(
        collection(db, "users"),
        where("uid", "==", currentUser.uid)
      );

      const snap = await getDocs(q);

      if (!snap.empty) {
        const userData = snap.docs[0].data();
        const userHostelId = userData.hostelId || "";

        setHostelId(userHostelId);

        setForm((prev) => ({
          ...prev,
          hostelId: userHostelId,
        }));

        await fetchHostels(userHostelId);
        await fetchRooms(userHostelId);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchHostels = async (hostelId) => {
    try {
      if (!hostelId) return;

      const q = query(
        collection(db, "hostels"),
        where("__name__", "==", hostelId)
      );

      const snap = await getDocs(q);

      const data = snap.docs.map((d) => ({
        id: d.id,
        name: d.data().hostelName,
      }));

      setHostels(data);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchRooms = async (hostelId) => {
    try {
      if (!hostelId) return;

      setLoading(true);

      const q = query(
        collection(db, "hostelrooms"),
        where("hostelId", "==", hostelId)
      );

      const snap = await getDocs(q);

      const data = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      setRooms(data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditId(null);
    setForm({
      hostelId: hostelId,
      roomNo: "",
      noOfBeds: "",
      maxStudents: "",
      status: "Available",
      schoolId: currentSchoolId,
    });
  };

  const handleSubmit = async () => {
    if (!form.roomNo || !form.noOfBeds) {
      return Swal.fire(
        "Validation Error",
        "Please fill required fields",
        "warning"
      );
    }

    try {
      setLoading(true);

      const payload = {
        ...form,
        noOfBeds: Number(form.noOfBeds),
        maxStudents: Number(form.maxStudents),
        updatedAt: Timestamp.now(),
      };

      if (editId) {
        await updateDoc(doc(db, "hostelrooms", editId), payload);
        Swal.fire("Success", "Room Updated Successfully", "success");
      } else {
        await addDoc(collection(db, "hostelrooms"), {
          ...payload,
          createdAt: Timestamp.now(),
        });
        Swal.fire("Success", "Room Added Successfully", "success");
      }

      setOpen(false);
      resetForm();
      fetchRooms(hostelId);
    } catch (error) {
      Swal.fire("Error", error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (row) => {
    setEditId(row.id);
    setForm({
      hostelId: row.hostelId,
      roomNo: row.roomNo || "",
      noOfBeds: row.noOfBeds || "",
      maxStudents: row.maxStudents || "",
      status: row.status || "Available",
      schoolId: row.schoolId || currentSchoolId,
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete Room?",
      text: "This action cannot be undone",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
    });

    if (!result.isConfirmed) return;

    try {
      await deleteDoc(doc(db, "hostelrooms", id));
      Swal.fire("Deleted", "Room Deleted Successfully", "success");
      fetchRooms(hostelId);
    } catch (error) {
      Swal.fire("Error", error.message, "error");
    }
  };

  const filteredRooms = rooms.filter((room) =>
    String(room.roomNo || "")
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  return (
    <Box p={1}>
      {/* Small margins and tiny text structure */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={1.5}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight="bold">
            Hostel Room Management
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Manage hostel rooms and bed capacity
          </Typography>
        </Box>

        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon fontSize="small" />}
          sx={{ bgcolor: PRIMARY, py: 0.5 }}
          onClick={() => {
            resetForm();
            setOpen(true);
          }}
        >
          Add Room
        </Button>
      </Stack>

      <Paper variant="outlined">
        {/* Compressed inner padding */}
        <Box p={1}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search Room Number..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ color: "gray", mr: 0.5, fontSize: 18 }} />
              ),
              style: { fontSize: "0.875rem" }
            }}
          />
        </Box>

        {/* Set Table size to small/dense */}
        <TableContainer>
          <Table size="small">
            <TableHead sx={{ bgcolor: "#f5f5f5" }}>
              <TableRow>
                <TableCell sx={{ py: 0.5, px: 1, fontWeight: "bold" }}>Sr.</TableCell>
                <TableCell sx={{ py: 0.5, px: 1, fontWeight: "bold" }}>Hostel</TableCell>
                <TableCell sx={{ py: 0.5, px: 1, fontWeight: "bold" }}>Room No</TableCell>
                <TableCell sx={{ py: 0.5, px: 1, fontWeight: "bold" }}>Beds</TableCell>
                <TableCell sx={{ py: 0.5, px: 1, fontWeight: "bold" }}>Max Students</TableCell>
                <TableCell sx={{ py: 0.5, px: 1, fontWeight: "bold" }}>Status</TableCell>
                <TableCell align="center" sx={{ py: 0.5, px: 1, fontWeight: "bold" }}>Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell align="center" colSpan={7} sx={{ py: 2 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : filteredRooms.length === 0 ? (
                <TableRow>
                  <TableCell align="center" colSpan={7} sx={{ py: 2, fontSize: "0.825rem" }}>
                    No Rooms Found
                  </TableCell>
                </TableRow>
              ) : (
                filteredRooms
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ py: 0.5, px: 1 }}>
                        {page * rowsPerPage + index + 1}
                      </TableCell>

                      <TableCell sx={{ py: 0.5, px: 1 }}>
                        {hostels.find((h) => h.id === row.hostelId)?.name || "-"}
                      </TableCell>

                      <TableCell sx={{ py: 0.5, px: 1 }}>{row.roomNo}</TableCell>
                      <TableCell sx={{ py: 0.5, px: 1 }}>{row.noOfBeds}</TableCell>
                      <TableCell sx={{ py: 0.5, px: 1 }}>{row.maxStudents}</TableCell>

                      <TableCell sx={{ py: 0.5, px: 1 }}>
                        <Chip
                          size="small"
                          label={row.status}
                          sx={{ height: 20, fontSize: "0.75rem" }}
                          color={
                            row.status === "Available"
                              ? "success"
                              : row.status === "Full"
                              ? "error"
                              : "warning"
                          }
                        />
                      </TableCell>

                      <TableCell align="center" sx={{ py: 0.5, px: 1 }}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEdit(row)}
                          sx={{ p: 0.25 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>

                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(row.id)}
                          sx={{ p: 0.25, ml: 0.5 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredRooms.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          size="small"
          slotProps={{
            select: {
              size: "small"
            }
          }}
        />
      </Paper>

      {/* Styled dense dialog form parameters */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ py: 1.5, px: 2, display: "flex", alignItems: "center", fontSize: "1rem" }}>
          <RoomIcon sx={{ mr: 1, fontSize: 20 }} />
          {editId ? "Update Room" : "Add Room"}
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ p: 2 }}>
          <Grid className="row" spacing={1.5}>
            <Grid className="col-sm-4 mb-3" item xs={12}>
              <TextField
                select
                fullWidth
                size="small"
                label="Hostel"
                value={form.hostelId}
                disabled
                InputLabelProps={{ style: { fontSize: "0.875rem" } }}
              >
                {hostels.map((h) => (
                  <MenuItem key={h.id} value={h.id} style={{ fontSize: "0.875rem" }}>
                    {h.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid className="col-sm-4 mb-3" item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Room Number"
                value={form.roomNo}
                onChange={(e) =>
                  setForm({ ...form, roomNo: e.target.value })
                }
                InputLabelProps={{ style: { fontSize: "0.875rem" } }}
              />
            </Grid>

            <Grid className="col-sm-4 mb-3" item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="No. of Beds"
                value={form.noOfBeds}
                onChange={(e) =>
                  setForm({ ...form, noOfBeds: e.target.value })
                }
                InputLabelProps={{ style: { fontSize: "0.875rem" } }}
              />
            </Grid>

            <Grid className="col-sm-4 mb-3" item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Max Students"
                value={form.maxStudents}
                onChange={(e) =>
                  setForm({
                    ...form,
                    maxStudents: e.target.value,
                  })
                }
                InputLabelProps={{ style: { fontSize: "0.875rem" } }}
              />
            </Grid>

            <Grid className="col-sm-4 mb-3" item xs={12}>
              <TextField
                select
                fullWidth
                size="small"
                label="Status"
                value={form.status}
                onChange={(e) =>
                  setForm({ ...form, status: e.target.value })
                }
                InputLabelProps={{ style: { fontSize: "0.875rem" } }}
              >
                <MenuItem value="Available" style={{ fontSize: "0.875rem" }}>Available</MenuItem>
                <MenuItem value="Full" style={{ fontSize: "0.875rem" }}>Full</MenuItem>
                <MenuItem value="Under Maintenance" style={{ fontSize: "0.875rem" }}>
                  Under Maintenance
                </MenuItem>
              </TextField>
            </Grid>

            <Grid className="col-sm-4 mb-3" item xs={12} sx={{ mt: 0.5 }}>
              <Button
                fullWidth
                variant="contained"
                size="small"
                sx={{ bgcolor: PRIMARY, py: 0.75 }}
                onClick={handleSubmit}
                disabled={loading}
              >
                {editId ? "Update Room" : "Save Room"}
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </Box>
  );
}